import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { Box, VStack, StackDivider } from "@chakra-ui/react";

import {
  AvailabilityCalendar,
  EmailCapture,
  FacilityAbout,
  FacilityHero,
  FreeToTrySection,
  HomeHero,
  HowItWorksVideo,
  NotificationProof,
  PhotoGallery,
  ReviewSection,
  WhyCancellations,
} from "#app/components/index.jsx";
import {
  buildFacilitySubtitle,
  buildGenericMetaDescription,
  FACILITY_SELECT,
} from "#app/components/landing/facilityLanding.server.js";
import {
  getLandingData,
  getLandingDataForFacilityGroup,
  LANDING_DATA_CACHE_CONTROL,
} from "#app/components/landing/landingLoader.server.js";
import { useOptionalUser } from "#app/hooks/useUser.js";
import { db } from "#app/utils/db.server.js";
import { notFound } from "#app/utils/response.server.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { FEATURED_FACILITIES } from "#app/utils/featuredFacilities.js";
import { getBlogPost } from "#app/utils/blog.js";
import { handleEmailCapture } from "#app/utils/emailCaptureAction.server.js";

export function meta({ data, matches }) {
  const baseUrl = getBaseUrl(matches);

  if (!data) {
    return getSeoMeta({
      title: "Campground not found | Schnerp",
      description: "The campground you're looking for could not be found.",
      image: getDefaultOgImage(baseUrl),
    });
  }

  return getSeoMeta({
    title: `${data.name} fully booked? Schnerp it! | Schnerp`,
    description: data.metaDescription,
    url: `${baseUrl}/featured/${data.routeSlug}`,
    image: getDefaultOgImage(baseUrl),
  });
}

export async function loader({ params }) {
  // Facility group (virtual slug spanning multiple facilities) → search variant.
  const featured = FEATURED_FACILITIES.find((f) => f.slug === params.slug);
  if (featured?.facilityMatch) {
    return loadFacilityGroup(params, featured);
  }

  const facility = await db.facility.findUnique({
    where: { slug: params.slug },
    select: FACILITY_SELECT,
  });
  if (!facility) {
    throw notFound({ error: "Campground not found" });
  }

  const effectiveFeatured =
    featured ||
    FEATURED_FACILITIES.find((f) => f.slug === facility.slug) ||
    null;
  const name = effectiveFeatured?.name || facility.name;
  const agencyName = effectiveFeatured?.agencyName || facility.agency.name;

  // Only pass a blog link through if the post exists and is published.
  const blogPostSlug = effectiveFeatured?.blogPostSlug || null;
  const validatedBlogPostSlug =
    blogPostSlug && (await getBlogPost(blogPostSlug)) ? blogPostSlug : null;

  const facilityPhotos = facility.extraAttributes?.photos || [];
  const facilityDescription = facility.extraAttributes?.description || null;
  const blurb = effectiveFeatured?.blurb || null;
  const subtitle = buildFacilitySubtitle(name, agencyName);
  // Meta priority: manual description > facility description > blurb > generic.
  const metaDescription =
    effectiveFeatured?.description ||
    facilityDescription ||
    blurb ||
    buildGenericMetaDescription(name);

  const baseData = await getLandingData(facility.agency.scanner, {
    tag: params.slug,
    facilityId: facility.id,
  });

  return json(
    {
      ...baseData,
      facilityId: facility.id,
      facilitySlug: facility.slug,
      name,
      subtitle,
      aboutHeading: effectiveFeatured?.aboutHeading || null,
      blurb,
      blogPostSlug: validatedBlogPostSlug,
      photos: facilityPhotos,
      emailCapture: effectiveFeatured?.emailCapture
        ? { enabled: true, ...effectiveFeatured.emailCapture }
        : null,
      metaDescription,
      routeSlug: facility.slug,
    },
    { headers: { "Cache-Control": LANDING_DATA_CACHE_CONTROL } },
  );
}

async function loadFacilityGroup(params, featured) {
  const facilities = await db.facility.findMany({
    where: { subName: featured.facilityMatch.subName },
    select: FACILITY_SELECT,
  });
  if (facilities.length === 0) {
    throw notFound({ error: "Campground not found" });
  }

  const facilityIds = facilities.map((f) => f.id);
  const name = featured.name;
  const agencyName = featured.agencyName || facilities[0].agency.name;

  const blogPostSlug = featured.blogPostSlug || null;
  const validatedBlogPostSlug =
    blogPostSlug && (await getBlogPost(blogPostSlug)) ? blogPostSlug : null;

  const blurb = featured.blurb || null;
  const subtitle = featured.subtitle || buildFacilitySubtitle(name, agencyName);
  const metaDescription =
    featured.description || blurb || buildGenericMetaDescription(name);

  const baseData = await getLandingDataForFacilityGroup(facilityIds, {
    tag: params.slug,
  });

  return json(
    {
      ...baseData,
      isGroup: true,
      name,
      subtitle,
      aboutHeading: featured.aboutHeading || null,
      blurb,
      blogPostSlug: validatedBlogPostSlug,
      metaDescription,
      routeSlug: featured.slug,
    },
    { headers: { "Cache-Control": LANDING_DATA_CACHE_CONTROL } },
  );
}

export async function action({ request, params }) {
  // For groups, look up any child facility to get the scanner.
  const featured = FEATURED_FACILITIES.find((f) => f.slug === params.slug);

  const facility = featured?.facilityMatch
    ? await db.facility.findFirst({
        where: { subName: featured.facilityMatch.subName },
        select: { agency: { select: { scanner: true } } },
      })
    : await db.facility.findUnique({
        where: { slug: params.slug },
        select: { agency: { select: { scanner: true } } },
      });

  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent === "emailCapture" && facility) {
    return handleEmailCapture(formData, {
      agency: facility.agency.scanner,
    });
  }

  return json({ error: "Unknown intent" }, { status: 400 });
}

export default function FeaturedFacilityPage() {
  const data = useLoaderData();
  const user = useOptionalUser();

  // Facility group: no single calendar — scope the search hero to the group's
  // facilities, then the standard search-page tail (mirrors the country pages).
  if (data.isGroup) {
    return (
      <>
        <VStack spacing={{ base: 10, md: 16 }} divider={<StackDivider />}>
          <HomeHero
            heading={data.name}
            subtitle={data.subtitle}
            facilityIds={data.facilityIds}
            reviews={data.reviews}
            stats={data.stats}
          />
          <HowItWorksVideo />
          <NotificationProof
            openings={data.recentOpenings}
            alertsSent={data.alertsSent}
          />
          <ReviewSection reviews={data.reviews} stats={data.stats} />
          <WhyCancellations />
          <FreeToTrySection />
        </VStack>
        {/* SEO context closes the page as headingless footer text — below the
            CTA and outside the divided stack, so no divider sits above it. */}
        <Box mt={{ base: 10, md: 16 }}>
          <FacilityAbout
            name={data.name}
            blurb={data.blurb}
            blogPostSlug={data.blogPostSlug}
          />
        </Box>
      </>
    );
  }

  // Single facility: the calendar is the funnel entry point. The hero CTA jumps
  // to it; the calendar already carries the "sold out doesn't mean forever"
  // message, so this variant skips WhyCancellations.
  const newPath = `/new/${data.facilitySlug}`;
  return (
    <>
      <VStack spacing={{ base: 10, md: 16 }} divider={<StackDivider />}>
        <FacilityHero
          name={data.name}
          subtitle={data.subtitle}
          reviews={data.reviews}
          stats={data.stats}
        />
        <AvailabilityCalendar
          facilityId={data.facilityId}
          facilitySlug={data.facilitySlug}
          facilityName={data.name}
        />
        <HowItWorksVideo />
        <NotificationProof
          openings={data.recentOpenings}
          alertsSent={data.alertsSent}
          ctaTo={newPath}
        />
        {data.photos?.length > 0 && (
          <PhotoGallery photos={data.photos} facilityName={data.name} />
        )}
        {data.emailCapture && !user && (
          <EmailCapture config={data.emailCapture} />
        )}
        <ReviewSection reviews={data.reviews} stats={data.stats} />
        <FreeToTrySection ctaTo={newPath} />
      </VStack>
      {/* SEO context closes the page as headingless footer text — below the
          CTA and outside the divided stack, so no divider sits above it. */}
      <Box mt={{ base: 10, md: 16 }}>
        <FacilityAbout
          name={data.name}
          blurb={data.blurb}
          blogPostSlug={data.blogPostSlug}
        />
      </Box>
    </>
  );
}
